import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

const DEFAULT_AVATAR = `${process.env.PUBLIC_URL}/logo512.png`;
const GAP = 4;
const VIEWPORT_MARGIN = 8;
const MAX_WIDTH = 320;

// Where the list goes: under the field if it fits, else above it (a
// composer pinned to the bottom of the screen), else on the roomier side
// with a capped height. Left-aligned with the field, inside the viewport.
export function computeSuggestionPosition({ anchor, menuHeight, viewportWidth, viewportHeight }) {
  const spaceBelow = viewportHeight - anchor.bottom - GAP - VIEWPORT_MARGIN;
  const spaceAbove = anchor.top - GAP - VIEWPORT_MARGIN;

  let placement;
  let maxHeight = null;
  if (menuHeight <= spaceBelow) placement = "bottom";
  else if (menuHeight <= spaceAbove) placement = "top";
  else {
    placement = spaceBelow >= spaceAbove ? "bottom" : "top";
    maxHeight = Math.max(0, placement === "bottom" ? spaceBelow : spaceAbove);
  }
  const height = maxHeight ?? menuHeight;
  const top = placement === "bottom" ? anchor.bottom + GAP : anchor.top - GAP - height;

  const width = Math.min(MAX_WIDTH, anchor.width, viewportWidth - 2 * VIEWPORT_MARGIN);
  const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left, viewportWidth - VIEWPORT_MARGIN - width));
  return { placement, top, left, width, maxHeight };
}

// The suggestion list of useMentionAutocomplete (spread its `suggestions`).
// Portaled to <body> with fixed positioning measured from the field, so a
// scrolling or overflow-hidden ancestor (focus views, modals) can't clip it;
// it follows the field on scroll and resize.
//
// Options take the mouse down without stealing focus from the field, so a
// click or tap selects and typing continues.
export default function MentionSuggestions({ id, optionId, anchorRef, users, highlighted, onHighlight, onSelect }) {
  const listRef = useRef(null);

  const place = useCallback(() => {
    const anchorEl = anchorRef.current;
    const list = listRef.current;
    if (!anchorEl || !list) return;
    list.style.maxHeight = "";
    const { placement, top, left, width, maxHeight } = computeSuggestionPosition({
      anchor: anchorEl.getBoundingClientRect(),
      menuHeight: list.getBoundingClientRect().height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });
    list.style.top = `${top}px`;
    list.style.left = `${left}px`;
    list.style.width = `${width}px`;
    list.style.maxHeight = maxHeight == null ? "" : `${maxHeight}px`;
    list.dataset.placement = placement;
  }, [anchorRef]);

  useLayoutEffect(() => {
    place();
  }, [place, users]);

  useEffect(() => {
    let frame = null;
    const schedule = () => {
      if (frame == null) {
        frame = window.requestAnimationFrame(() => {
          frame = null;
          place();
        });
      }
    };
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      if (frame != null) window.cancelAnimationFrame(frame);
    };
  }, [place]);

  // Keep the highlighted option in view while arrowing through.
  useEffect(() => {
    document.getElementById(optionId(highlighted))?.scrollIntoView?.({ block: "nearest" });
  }, [highlighted, optionId]);

  return createPortal(
    <ul
      ref={listRef}
      id={id}
      role="listbox"
      aria-label="Mention suggestions"
      className="fixed top-0 left-0 z-[70] overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-white py-1 shadow-md"
    >
      {users.map((user, index) => (
        <li
          key={user.user_id ?? user.id}
          id={optionId(index)}
          role="option"
          aria-selected={index === highlighted}
          aria-label={`@${user.username}`}
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => onHighlight(index)}
          onClick={() => onSelect(user)}
          className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm ${
            index === highlighted ? "bg-gray-100" : ""
          }`}
        >
          <img
            src={user.profile_pic_url || DEFAULT_AVATAR}
            alt=""
            className="h-6 w-6 flex-shrink-0 rounded-full border border-black object-cover"
          />
          <span className="truncate font-semibold">@{user.username}</span>
        </li>
      ))}
    </ul>,
    document.body
  );
}
